import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/functions/_generated/api';
import { AIModProvider, adminSession } from '@/lib/api';
import { friendlyError } from '@/lib/errors';
import { DMReportsPanel } from './DMReports';

/**
 * Admin-side picker for the rating-note moderation provider. Reads
 * api.admin.moderation.getAIModConfigQ to populate the list; the
 * setAIModProviderM mutation flips the choice + the new provider is
 * used on the very next /rate call.
 *
 * Clicks on a configured row don't flip the provider instantly — they
 * open a small contextual popover anchored to the row with
 * "Switch to <provider>?" + Switch / Cancel buttons. Stops accidental
 * provider flips on a panel where every action is a config write.
 *
 * Providers without an API key are listed but disabled — gives the
 * admin a one-glance view of "what's wired up on this environment"
 * without us leaking the keys themselves.
 */
const MOD_TABS: { id: 'notes' | 'dm'; label: string }[] = [
  { id: 'notes', label: 'Rating notes' },
  { id: 'dm', label: 'DM reports' },
];

/**
 * Moderation shell with two tabs: "Rating notes" (the AI-mod provider picker)
 * and "DM reports" (abuse reports from DM threads).
 */
export function ModerationPage() {
  const [tab, setTab] = useState<'notes' | 'dm'>('notes');
  return (
    <div>
      <h2 className="serif" style={{ fontSize: 28, fontWeight: 500, fontStyle: 'italic', margin: 0, marginBottom: 16 }}>
        Moderation
      </h2>
      <div style={{ display: 'inline-flex', gap: 4, background: 'var(--surface-2)', padding: 4, borderRadius: 10, marginBottom: 22, maxWidth: '100%', overflowX: 'auto' }}>
        {MOD_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            style={{
              padding: '6px 14px',
              borderRadius: 8,
              border: 'none',
              background: tab === t.id ? 'var(--surface)' : 'transparent',
              color: tab === t.id ? 'var(--ink)' : 'var(--ink-3)',
              fontSize: 13,
              fontWeight: tab === t.id ? 600 : 500,
              fontFamily: 'var(--font-ui)',
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'notes' ? <RatingNotesPanel /> : <DMReportsPanel />}
    </div>
  );
}

function RatingNotesPanel() {
  const token = adminSession.getToken() ?? '';
  const cfg = useQuery(
    api.admin.moderation.getAIModConfigQ,
    token ? { token } : 'skip',
  );
  const setProviderMut = useMutation(api.admin.moderation.setAIModProviderM);

  const loading = cfg === undefined;
  const [saving, setSaving] = useState<AIModProvider | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [savedToast, setSavedToast] = useState<string | null>(null);

  // Pending switch — the provider the admin tapped + the row element
  // the popover should anchor to. null = no popover open.
  const [pending, setPending] = useState<{ provider: AIModProvider; anchor: HTMLElement } | null>(null);

  const choose = async (provider: AIModProvider) => {
    if (saving) return;
    setSaving(provider);
    setErr(null);
    setPending(null);
    try {
      const next = await setProviderMut({ token, provider });
      setSavedToast(`Switched to ${next.providers.find((p) => p.id === provider)?.label ?? provider}.`);
      setTimeout(() => setSavedToast(null), 2500);
    } catch (e) {
      setErr(friendlyError(e, 'Moderation'));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div>
      <p style={{ color: 'var(--ink-3)', fontSize: 13, marginBottom: 24 }}>
        Picks the LLM that classifies anonymous rater notes before they reach the rated user. Switch takes effect on the next rating submitted.
      </p>

      {err && (
        <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '10px 14px', borderRadius: 12, fontSize: 13, marginBottom: 16 }}>
          {err}
        </div>
      )}
      {savedToast && (
        <div style={{ background: 'rgba(56,142,60,0.10)', color: '#2e7d32', padding: '10px 14px', borderRadius: 12, fontSize: 13, marginBottom: 16 }}>
          {savedToast}
        </div>
      )}

      <div style={{ background: 'var(--surface)', borderRadius: 14, border: '1px solid var(--line)', padding: 6 }}>
        {loading || !cfg ? (
          <div style={{ padding: 24, color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {cfg.providers.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={(e) => {
                  if (!p.configured || p.active || saving !== null) return;
                  setPending({ provider: p.id, anchor: e.currentTarget });
                }}
                disabled={!p.configured || p.active || saving !== null}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '14px 16px',
                  borderRadius: 10,
                  border: 'none',
                  background: p.active ? 'var(--bg)' : 'transparent',
                  cursor: !p.configured || p.active ? 'default' : 'pointer',
                  textAlign: 'left',
                  opacity: !p.configured ? 0.5 : 1,
                  fontFamily: 'var(--font-ui)' }}
              >
                <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                  <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--ink)' }}>
                    {p.label}
                  </div>
                  <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4, letterSpacing: 0.5 }}>
                    {p.model}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  {!p.configured && <Tag>NO API KEY</Tag>}
                  {saving === p.id && <Tag color="coral">SAVING…</Tag>}
                  {p.active && saving !== p.id && <Tag color="green">ACTIVE</Tag>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {pending && cfg && (
        <ConfirmSwitchPopover
          anchor={pending.anchor}
          label={cfg.providers.find((p) => p.id === pending.provider)?.label ?? pending.provider}
          onCancel={() => setPending(null)}
          onConfirm={() => void choose(pending.provider)}
        />
      )}
    </div>
  );
}

// ConfirmSwitchPopover — small contextual popover anchored to the
// clicked row. Portalled to document.body so it always renders on
// top + isn't clipped by the row's stacking context.
function ConfirmSwitchPopover({
  anchor, label, onCancel, onConfirm }: {
  anchor: HTMLElement;
  label: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Anchor to the right edge of the clicked row, vertically centred.
  useLayoutEffect(() => {
    const update = () => {
      const r = anchor.getBoundingClientRect();
      const popW = 260;
      const popH = 110;
      // Prefer popover to the LEFT of the row's right edge so it doesn't
      // spill off the right of the sidebar layout. Vertically aligned
      // with the row centre.
      let left = r.right - popW;
      const top = r.top + r.height / 2 - popH / 2;
      // Clamp to viewport.
      if (left < 8) left = 8;
      if (left + popW > window.innerWidth - 8) left = window.innerWidth - 8 - popW;
      const safeTop = Math.max(8, Math.min(top, window.innerHeight - 8 - popH));
      setPos({ top: safeTop, left });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchor]);

  // Click-outside + Escape close.
  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (ref.current?.contains(t)) return;
      if (anchor.contains(t)) return; // the trigger row
      onCancel();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onCancel]);

  if (!pos) return null;

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-modal="false"
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: 260,
        background: 'var(--surface)',
        border: '1px solid var(--line-strong)',
        borderRadius: 14,
        boxShadow: '0 16px 40px rgba(0,0,0,0.18)',
        padding: 14,
        zIndex: 1000 }}
    >
      <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.45, marginBottom: 12 }}>
        Switch active provider to <strong style={{ color: 'var(--ink)' }}>{label}</strong>?
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: '7px 12px',
            background: 'transparent',
            border: 'none',
            color: 'var(--ink-3)',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'var(--font-ui)' }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          style={{
            padding: '7px 14px',
            background: 'var(--coral)',
            color: '#fff',
            border: 'none',
            borderRadius: 100,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'var(--font-ui)' }}
        >
          Switch
        </button>
      </div>
    </div>,
    document.body,
  );
}

function Tag({ children, color }: { children: React.ReactNode; color?: 'coral' | 'green' }) {
  const palette = color === 'coral'
    ? { bg: 'rgba(232,106,76,0.12)', fg: '#a8331f' }
    : color === 'green'
      ? { bg: 'rgba(56,142,60,0.12)', fg: '#2e7d32' }
      : { bg: 'var(--chip)', fg: 'var(--ink-3)' };
  return (
    <span
      className="mono"
      style={{
        display: 'inline-block',
        padding: '3px 9px',
        borderRadius: 100,
        background: palette.bg,
        color: palette.fg,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 1 }}
    >
      {children}
    </span>
  );
}
